"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { CheckCircle2, Clock, Loader2 } from "lucide-react";

import { FieldError } from "@/shared/form/FieldError";
import { formatTimer } from "@/shared/utils/formatTimer";
import { formatPhoneNumber, unformatPhoneNumber } from "@/shared/utils/phone";

import { useBrandSmsVerification } from "@/features/admin/hooks/useBrandSmsVerification";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function AdminForgotPasswordPage() {
  const router = useRouter();
  const {
    email,
    setEmail,
    phoneNumber,
    setPhoneNumber,
    code,
    setCode,
    isVerified,
    isExpired,
    timer,
    isSending,
    isVerifying,
    sendSms,
    verifySms,
  } = useBrandSmsVerification();

  function handleProceed() {
    sessionStorage.setItem(
      "brandPwReset",
      JSON.stringify({ email: email.trim().toLowerCase(), phoneNumber, code }),
    );
    router.push("/admin/reset-password");
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4 py-8">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center space-y-3 pb-4">
          <div className="flex items-center gap-2">
            <span className="font-heading text-2xl font-bold tracking-tight">dangil</span>
            <span className="rounded bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
              Admin
            </span>
          </div>
          <div className="space-y-1 text-center">
            <h1 className="font-heading text-2xl font-semibold tracking-tight text-neutral-900">
              브랜드 비밀번호 찾기
            </h1>
            <p className="text-sm text-muted-foreground">
              등록된 휴대폰 번호로 인증 후 비밀번호를 재설정하세요
            </p>
          </div>
        </CardHeader>

        <CardContent>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-sm font-medium text-neutral-700">
                이메일
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="브랜드 로그인 이메일"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isVerified}
                className="h-10 rounded-md"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="phoneNumber" className="text-sm font-medium text-neutral-700">
                휴대폰 번호
              </Label>
              <div className="flex gap-2">
                <Input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  placeholder="010-1234-5678"
                  value={formatPhoneNumber(phoneNumber)}
                  onChange={(e) => setPhoneNumber(unformatPhoneNumber(e.target.value))}
                  disabled={isVerified}
                  className="h-10 flex-1 rounded-md"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={sendSms}
                  disabled={!email || !phoneNumber || isSending || isVerified}
                  className="h-10 shrink-0 gap-1.5 rounded-md border-neutral-200 font-medium text-neutral-700 hover:border-primary hover:text-primary"
                >
                  {isSending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : timer > 0 && !isVerified ? (
                    <>
                      <Clock className="h-3.5 w-3.5 text-neutral-400" />
                      <span className="tabular-nums">{formatTimer(timer)}</span>
                    </>
                  ) : (
                    "인증번호 발송"
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="code" className="text-sm font-medium text-neutral-700">
                  인증번호
                </Label>
                {timer > 0 && !isVerified && (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {formatTimer(timer)} 후 만료
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <Input
                  id="code"
                  name="code"
                  type="text"
                  inputMode="numeric"
                  placeholder="인증번호 6자리"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  disabled={isVerified}
                  className="h-10 flex-1 rounded-md"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={verifySms}
                  disabled={!code || timer === 0 || isVerifying || isVerified}
                  className="h-10 shrink-0 rounded-md border-neutral-200 font-medium text-neutral-700 hover:border-primary hover:text-primary"
                >
                  {isVerifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "인증"}
                </Button>
              </div>

              {isVerified && (
                <div className="flex items-center gap-1.5 rounded-md bg-success/10 px-3 py-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-success" />
                  <p className="text-sm font-medium text-success">인증이 완료되었습니다</p>
                </div>
              )}

              {isExpired && !isVerified && (
                <FieldError message="인증시간이 만료되었습니다. 인증번호를 다시 발송해주세요." />
              )}
            </div>

            <Button
              type="button"
              className="h-10 w-full bg-primary font-medium text-white hover:bg-primary/90"
              disabled={!isVerified}
              onClick={handleProceed}
            >
              비밀번호 재설정
            </Button>
          </div>

          <div className="mt-5 text-center">
            <Link
              href="/admin/login"
              className="text-sm text-muted-foreground transition-colors hover:text-primary"
            >
              로그인으로 돌아가기
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
